import { styled } from '@mui/material';
import { Box, Divider, IconButton, Typography } from '@packages/shared';
import { Minus32, Plus32 } from '@packages/themes/icons';
import { useAtom } from 'jotai';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { selectedQuantity } from '../../atoms';
import { getValidQuantity, getValidValues } from './helper';

const StyledIconButton = styled(IconButton)`
  width: 1.75rem;
  height: 1.75rem;
  min-height: 1.75rem;
`;

export type QuantitySelectionProps = {
  /**
   * Sets the maximum value the quantity can be.
   * @default 10
   */
  max?: number;
  /**
   * Sets the minimum value the quantity can be.
   * @default 1
   */
  min?: number;
  /**
   * Sets mow much the quantity of the step will be increased/decreased on button click.
   * @default 1
   */
  step?: number;
};

/**
 * <p>
 * This component is used to display some quantity. It comes with an increase and decrease button.
 * With the given props, you can adjust the <code>step</code> value with which the quantity will be decreased/increased.
 * You also can set a <code>min</code>/<code>max</code> value for the quantity and define the initial quantity with the <code>quantity</code> prop.
 * </p>
 * */
export const QuantitySelection: FC<QuantitySelectionProps> = ({ step = 1, min = 1, max = 10 }) => {
  const values = getValidValues({
    step,
    min,
    max,
  });
  const [quantity, setQuantityAtom] = useAtom(selectedQuantity);

  return (
    <>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          my: 1.5,
        }}
      >
        <Typography sx={{ mx: 2, userSelect: 'none' }} variant="body2">
          <FormattedMessage defaultMessage="Anzahl" id="quantitySelection.label" />
        </Typography>
        <StyledIconButton
          onClick={() => {
            setQuantityAtom(
              getValidQuantity({
                currentQuantity: quantity,
                type: 'decrease',
                values: { max: values.max, min: values.min, step: values.step },
              }),
            );
          }}
          disabled={quantity <= values.min}
          icon={<Minus32 />}
        />
        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'grey.dark',
            borderRadius: '3px',
            overflow: 'hidden',
            fontSize: '0.813rem',
            mx: 1.5,
            width: '3.5rem',
            height: ' 1.75rem',
            textAlign: 'center',
            userSelect: 'none',
          }}
          variant="body2"
        >
          {quantity}
        </Typography>
        <StyledIconButton
          onClick={() => {
            setQuantityAtom(
              getValidQuantity({
                currentQuantity: quantity,
                type: 'increase',
                values: { max: values.max, min: values.min, step: values.step },
              }),
            );
          }}
          disabled={quantity >= values.max}
          icon={<Plus32 />}
        />
      </Box>
      <Divider />
    </>
  );
};
